import React, { useRef, useEffect } from "react";

const STAR_COUNT = 120;
const STAR_COLORS = [
  "rgba(255,255,255,0.9)",
  "rgba(180,200,255,0.7)",
  "rgba(200,180,255,0.6)"
];
const SHOOTING_STAR_COUNT = 3;

function randomBetween(a: number, b: number) {
  return a + Math.random() * (b - a);
}

function randomShootingStar(width: number, height: number) {
  // Start and end anywhere on screen, always white and glowing
  const startX = Math.random();
  const startY = Math.random();
  const endX = Math.random();
  const endY = Math.random();
  const dx = endX - startX;
  const dy = endY - startY;
  const duration = Math.random() * 1 + 2.2; // 2.2s to 3.2s
  return {
    startX,
    startY,
    dx,
    dy,
    length: Math.random() * 80 + 80,
    alpha: 0,
    phase: 0,
    duration,
    startTime: Date.now() + Math.random() * 7000 + 2000, // random delay
    color: 'rgba(255,255,255,1)'
  };
}

export const StarryNight: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const stars = useRef(
    Array.from({ length: STAR_COUNT }, () => ({
      x: Math.random(),
      y: Math.random(),
      r: randomBetween(0.7, 2.2),
      baseAlpha: randomBetween(0.5, 1),
      twinkleSpeed: randomBetween(0.8, 2.5),
      twinklePhase: randomBetween(0, Math.PI * 2),
      color: STAR_COLORS[Math.floor(Math.random() * STAR_COLORS.length)]
    }))
  );
  const shootingStars = useRef<any[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let width = window.innerWidth;
    let height = window.innerHeight;
    let running = true;

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      if (canvas) {
        canvas.width = width;
        canvas.height = height;
      }
    }
    resize();
    window.addEventListener("resize", resize);

    function draw() {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      const now = Date.now() * 0.001;
      // Draw stars
      for (const star of stars.current) {
        const twinkle = Math.sin(now * star.twinkleSpeed + star.twinklePhase) * 0.45 + 0.55;
        ctx.save();
        ctx.beginPath();
        ctx.arc(star.x * width, star.y * height, star.r, 0, Math.PI * 2);
        ctx.shadowColor = star.color;
        ctx.shadowBlur = 16 + 8 * twinkle;
        ctx.fillStyle = star.color;
        ctx.globalAlpha = star.baseAlpha * twinkle;
        ctx.fill();
        ctx.restore();
      }
      // Draw shooting stars
      const t = Date.now();
      if (shootingStars.current.length < SHOOTING_STAR_COUNT) {
        shootingStars.current.push(randomShootingStar(width, height));
      }
      for (let i = shootingStars.current.length - 1; i >= 0; i--) {
        const s = shootingStars.current[i];
        const elapsed = (t - s.startTime) / 1000;
        if (elapsed < 0) continue; // not started yet
        if (elapsed > s.duration) {
          shootingStars.current.splice(i, 1);
          continue;
        }
        // Fade in/out
        let alpha = 1;
        if (elapsed < 0.2) alpha = elapsed / 0.2;
        else if (elapsed > s.duration - 0.3) alpha = (s.duration - elapsed) / 0.3;
        // Head position
        const progress = elapsed / s.duration;
        const px = (s.startX + s.dx * progress) * width;
        const py = (s.startY + s.dy * progress) * height;
        // Trail
        const trailLength = s.length;
        const trailSteps = 18;
        for (let j = 0; j < trailSteps; j++) {
          const trailProgress = j / trailSteps;
          const tx = px - s.dx * trailLength * trailProgress;
          const ty = py - s.dy * trailLength * trailProgress;
          ctx.save();
          ctx.globalAlpha = alpha * (1 - trailProgress) * 0.25;
          ctx.beginPath();
          ctx.arc(tx, ty, 2.2 - 1.5 * trailProgress, 0, Math.PI * 2);
          ctx.fillStyle = s.color;
          ctx.shadowColor = s.color;
          ctx.shadowBlur = 12 * (1 - trailProgress);
          ctx.fill();
          ctx.restore();
        }
        // Head (bright dot)
        ctx.save();
        ctx.globalAlpha = alpha * 0.85;
        ctx.beginPath();
        ctx.arc(px, py, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = s.color;
        ctx.shadowColor = s.color;
        ctx.shadowBlur = 24;
        ctx.fill();
        ctx.restore();
      }
    }

    function animate() {
      if (!running) return;
      draw();
      animationRef.current = requestAnimationFrame(animate);
    }
    animate();
    return () => {
      running = false;
      window.removeEventListener("resize", resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 20,
        pointerEvents: "none",
        background: "transparent"
      }}
    />
  );
}; 